import { useEffect, useState } from "react";
import { CheckCircle2, Sparkles, GraduationCap, Award } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import Seo, { breadcrumbLd } from "@/components/vr/Seo";

const DEFAULT_BENEFITS = [
  "Grammar सम्पूर्ण नियम — Tense, Voice, Narration, Article",
  "Board Answer Writing की सटीक Technique",
  "50+ Important Questions with Model Answers",
  "Easy Tricks एवं Shortcuts",
  "Bihar Board Exam Strategy 2027",
  "Chapter-wise Practice Sets",
  "Board Pattern Model Paper",
  "Direct Faiz Sir के नोट्स एवं PDF",
];

export default function EnglishCourse() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [course, setCourse] = useState(null);
  useEffect(() => {
    api.get("/courses/bihar-board-english-course-2027").then((r) => setCourse(r.data)).catch(() => setCourse(null));
  }, []);
  const c = course || {};
  const price = c.price ?? 199;
  const orig = c.original_price ?? 999;
  const off = orig > price ? Math.round(((orig - price) / orig) * 100) : 0;
  const benefits = (c.benefits && c.benefits.length) ? c.benefits : DEFAULT_BENEFITS;
  const cta = c.cta_text || "BUY COURSE NOW";
  const name = c.name || "Bihar Board English Course 2027";
  const desc = c.description || "कक्षा 10 एवं 12 के छात्रों के लिए विशेष रूप से डिज़ाइन किया गया English Course।";
  const courseLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name,
    description: desc,
    provider: { "@type": "Organization", name: "VR Classes", url: typeof window !== "undefined" ? window.location.origin : undefined },
    offers: { "@type": "Offer", price: String(price), priceCurrency: "INR", availability: "https://schema.org/InStock" },
  };

  const buy = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/course/order", form);
      if (!data.gateway_ready) {
        setResult({ pending: true, message: data.message });
      } else if (window.Razorpay) {
        const opts = {
          key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
          name: "VR Classes", description: "Bihar Board English Course 2027",
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#DC2626" },
          handler: () => setResult({ pending: false, message: "🎉 Payment successful! We will email you the course access shortly." }),
        };
        new window.Razorpay(opts).open();
      } else {
        setResult({ pending: true, message: "Razorpay script not loaded. Please try again." });
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="vr-container my-8">
      <Seo
        title={`${name} — ₹${price}`}
        description={desc}
        canonical="/english-course"
        jsonLd={[breadcrumbLd([{ name: "Home", url: "/" }, { name: "English Course", url: "/english-course" }]), courseLd]}
      />
      <Breadcrumbs items={[{ label: "English Course" }]} />
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-[#DC2626] text-xs font-bold"><Sparkles size={14} /> Special Course 2027</div>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A]">{name}</h1>
          <p className="mt-2 text-slate-600 font-hindi text-lg">{desc}</p>

          <div className="mt-6 vr-card p-5">
            <div className="flex items-baseline gap-3">
              <div className="font-display font-black text-4xl text-[#DC2626]">₹{price}</div>
              {orig > price && <div className="line-through text-slate-400">₹{orig}</div>}
              {off > 0 && <span className="ml-auto text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded">{off}% OFF</span>}
            </div>
            <div className="mt-1 text-xs text-slate-500">One-time payment · Lifetime access</div>
          </div>

          <div className="mt-6">
            <h3 className="font-display font-bold text-lg">कोर्स में क्या मिलेगा?</h3>
            <ul className="mt-3 space-y-2">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 font-hindi text-slate-700"><CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" /> {b}</li>
              ))}
            </ul>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[{ n: "500+", l: "Students Enrolled" }, { n: "50+", l: "Video Lessons" }, { n: "24/7", l: "Support" }].map((s, i) => (
              <div key={i} className="vr-card p-3 text-center">
                <div className="font-display font-extrabold text-xl text-[#1E3A8A]">{s.n}</div>
                <div className="text-xs text-slate-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 self-start">
          <div className="vr-card p-6">
            <div className="flex items-center gap-2"><Award className="text-[#DC2626]" size={20} /> <span className="font-bold text-[#0F172A]">Enroll Now</span></div>
            <form onSubmit={buy} className="mt-4 space-y-3">
              <input required placeholder="आपका नाम" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-12 px-4 border border-slate-200 rounded-lg font-hindi" data-testid="course-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-12 px-4 border border-slate-200 rounded-lg" data-testid="course-email" />
              <input required placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full h-12 px-4 border border-slate-200 rounded-lg" data-testid="course-phone" />
              {err && <div className="text-sm text-red-600" data-testid="course-error">{err}</div>}
              <button disabled={loading} type="submit" className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-lg disabled:opacity-60" data-testid="course-buy-btn">
                <GraduationCap size={20} /> {loading ? "Processing..." : `${cta} — ₹${price}`}
              </button>
              <div className="text-xs text-slate-500 text-center">100% secure payment · No card details stored</div>
            </form>
            {result && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${result.pending ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`} data-testid="course-result">
                {result.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
